// Shared "Correo secundario" modal (PATCH /admin/users/:id/secondary-email
// via setSecondaryEmail()). Used by both Users.tsx's row action and
// UserDetail.tsx's inline "Editar" button on the Información card.

import { useEffect, useState } from 'react'

import { Button, FormField, Input, Modal, toast } from '../../../design-system/index.js'
import { apiErrorMessage } from '../../../lib/api-error.js'
import { setSecondaryEmail } from '../users-data.js'

// Client-side pre-check only — the API is still the source of truth.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface SecondaryEmailModalTarget {
  id: string
  name: string
  secondary_email: string | null
}

export interface SecondaryEmailModalProps {
  target: SecondaryEmailModalTarget | null
  onClose: () => void
  onSuccess: () => void | Promise<void>
}

export function SecondaryEmailModal({
  target,
  onClose,
  onSuccess,
}: SecondaryEmailModalProps): JSX.Element {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (target) setValue(target.secondary_email ?? '')
  }, [target])

  function close(): void {
    setValue('')
    onClose()
  }

  const trimmed = value.trim()
  const invalid = trimmed !== '' && !EMAIL_RE.test(trimmed)

  async function submit(): Promise<void> {
    if (!target || invalid) return
    setBusy(true)
    try {
      await setSecondaryEmail(target.id, trimmed || null)
      toast.ok('Correo secundario guardado')
      close()
      await onSuccess()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo guardar el correo secundario.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={target !== null}
      onClose={close}
      title="Correo secundario"
      {...(target
        ? {
            subtitle: `Destino de notificaciones para ${target.name}. Déjalo vacío para quitarlo.`,
          }
        : {})}
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={close} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} loading={busy} disabled={invalid}>
            Guardar
          </Button>
        </>
      }
    >
      <FormField
        label="Correo secundario"
        htmlFor="secondary-email-input"
        {...(invalid ? { error: 'Escribe un correo válido.' } : {})}
      >
        <Input
          id="secondary-email-input"
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="otro-correo@ejemplo.com"
        />
      </FormField>
    </Modal>
  )
}
